'use client';

import {type FC, useCallback, useState} from 'react';
import clsx from 'clsx';

import {Button} from '@/modules/foundation/components/button';
import type {ContainerSpacing} from '@/modules/foundation/components/container';
import {Container} from '@/modules/foundation/components/container';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import type {Article} from '../../shared/types';
import {ArticleCard} from '../article-card';

interface Props {
	spacingTop?: ContainerSpacing;
	spacingBottom?: ContainerSpacing;
	limit?: number;
	articles: KeyedArray<Article>;
}

const ArticleListPaginated: FC<Props> = ({spacingBottom, spacingTop, limit, articles}) => {
	const [visiblePages, setVisiblePages] = useState(0);

	let visibleArticles = articles;

	if (limit) {
		visibleArticles = articles?.slice(0, limit);
		for (let i = 0; i < visiblePages; i++) {
			if (i + limit < articles.length) {
				visibleArticles.push(articles[i + limit]);
			}
		}
	}

	const loadMore = useCallback(() => {
		// loadMore will never be called if limit is 0 or undefined therefor the default limit does not matter
		setVisiblePages((prev) => prev + (limit ?? 2));
	}, [limit]);

	if (!visibleArticles) return null;

	const totalArticles = articles?.length;

	const hasMore = visibleArticles.length < totalArticles;

	const renderedArticles = visibleArticles;

	return (
		<Container spacingBottom={spacingBottom} spacingTop={spacingTop}>
			<ul className={clsx('grid', 'auto-rows-fr', 'grid-cols-1', 'lg:grid-cols-2', 'gap-14')}>
				<>
					{renderedArticles.map((article) => {
						if (!article) {
							return null;
						}

						return (
							<li key={article._key}>
								<ArticleCard article={article} headingLevel={2} />
							</li>
						);
					})}
				</>
			</ul>

			{hasMore && (
				<Button onClick={loadMore} className={clsx('justify-self-start', 'mt-12')}>
					Vis flere blogginnlegg
				</Button>
			)}
		</Container>
	);
};

export default ArticleListPaginated;
